import type { ShouldRevalidateFunctionArgs } from "react-router";
import { filterParamKeys } from "./constants";

/**
 * Options for configuring the revalidation behavior
 */
interface CustomRevalidationOptions {
  /**
   * URL parameters to ignore when determining whether to revalidate
   */
  ignoreParams?: string[];

  /**
   * URL parameters to always revalidate on, regardless of other settings
   */
  alwaysRevalidateParams?: string[];

  /**
   * Whether to always revalidate when a form is submitted
   */
  revalidateOnFormSubmit?: boolean;
}

/**
 * Utility function to handle route revalidation logic
 * Prevents unnecessary revalidation when only specified URL parameters change,
 * while ensuring we revalidate when important filter parameters change
 */
export function createShouldRevalidate({
  // ignoreParams = ["tab"],
  ignoreParams = [],
  alwaysRevalidateParams = ["page", "pageSize", ...filterParamKeys],
  revalidateOnFormSubmit = true,
}: CustomRevalidationOptions = {}) {
  return function shouldRevalidate({
    currentParams,
    nextParams,
    currentUrl,
    nextUrl,
    formMethod,
    formAction,
    defaultShouldRevalidate,
  }: ShouldRevalidateFunctionArgs) {
    // Always revalidate for form submissions if specified
    if (revalidateOnFormSubmit && formMethod && formAction) {
      return true;
    }

    // If route params changed, use default behavior
    if (
      !Object.keys(currentParams).every(
        (param) => currentParams[param] === nextParams[param]
      )
    ) {
      return defaultShouldRevalidate;
    }

    // If pathname changed, use default behavior
    if (currentUrl.pathname !== nextUrl.pathname) {
      return defaultShouldRevalidate;
    }

    // If search params haven't changed, use default behavior
    if (currentUrl.search === nextUrl.search) {
      return defaultShouldRevalidate;
    }

    // At this point, we know:
    // 1. Route params are the same
    // 2. Pathname is the same
    // 3. Search params are different

    const nextSearchParams = new URLSearchParams(nextUrl.search);
    const currentSearchParams = new URLSearchParams(currentUrl.search);

    // Check if any alwaysRevalidateParams have changed
    for (const param of alwaysRevalidateParams) {
      // For multi-value parameters like 'status'
      const nextValues = nextSearchParams.getAll(param);
      const currentValues = currentSearchParams.getAll(param);

      // If the parameter can have multiple values (like 'status')
      if (nextValues.length > 0 || currentValues.length > 0) {
        // Sort the values to ensure consistent comparison
        const nextSorted = [...nextValues].sort().join(",");
        const currentSorted = [...currentValues].sort().join(",");

        if (nextSorted !== currentSorted) {
          return true; // Revalidate if values changed
        }
        continue;
      }

      // Single value parameter comparison
      if (nextSearchParams.get(param) !== currentSearchParams.get(param)) {
        return true; // Revalidate if the value changed
      }
    }

    // Check if only ignored params changed
    const changedParams = new Set<string>();

    // Add changed or added params
    for (const [key, value] of nextSearchParams) {
      if (currentSearchParams.get(key) !== value) {
        changedParams.add(key);
      }
    }

    // Add deleted params
    for (const [key] of currentSearchParams) {
      if (!nextSearchParams.has(key)) {
        changedParams.add(key);
      }
    }

    // If only ignored params changed, don't revalidate
    if (
      Array.from(changedParams).every((param) => ignoreParams.includes(param))
    ) {
      return false;
    }

    // Otherwise, use default behavior
    return defaultShouldRevalidate;
  };
}
